import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, input, Input, OnInit, Output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataNoRowOverlayComponent, ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { connectState, InputObservable } from '@examdojo/angular/util';
import { AnimationDelay, AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { EmptyStateComponent } from '@examdojo/core/empty-state';
import { LoaderComponent } from '@examdojo/core/loader';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { HasEventTargetAddRemove } from 'rxjs/internal/observable/fromEvent';
import { AbstractListViewService } from '../abstract-list-view.service';
import { ListViewDataBase, ListViewGridMessages } from '../master-list-view.model';

@UntilDestroy()
@Component({
  selector: 'dojo-list-view-grid',
  imports: [
    NgIf,
    MatButtonModule,
    ExamdojoAgGridModule,
    EmptyStateComponent,
    MatTooltipModule,
    NgClass,
    LoaderComponent,
    ButtonComponent,
  ],
  templateUrl: './list-view-grid.component.html',
  styleUrls: ['./list-view-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [getAnimationAppearDisappear({ enterDelay: AnimationDelay.Short, leave: AnimationDuration.None })],
})
export class ListViewGridComponent<D extends ListViewDataBase = ListViewDataBase> implements OnInit {
  constructor(public readonly service: AbstractListViewService<D>) {}

  @Input() name = '';
  @Input() columnDefs: ColDef[] = [];
  readonly gridOptions = input.required<GridOptions>();
  @Input() components: GridOptions['components'] = {
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
  };
  @Input() messages?: ListViewGridMessages;

  @Input() searchCtrl!: FormControl<string>;
  @Input() hideLoader = false;

  @Input()
  @InputObservable()
  incrementalLoading = false;
  private readonly incrementalLoading$!: Observable<boolean>;

  @Input() canAdd = true;
  @Input() agGridClass = 'ag-theme-basic-table';
  @Input() agGridAdditionalClass? = '';

  @Output() add = new EventEmitter<void>();
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() gridDestroy = new EventEmitter<void>();
  @Output() gridRowClicked = new EventEmitter<RowClickedEvent>();
  @Output() gridRowDoubleClicked = new EventEmitter<RowDoubleClickedEvent>();
  @Output() gridSelectionChanged = new EventEmitter<SelectionChangedEvent>();
  @Output() cellClicked = new EventEmitter<CellClickedEvent>();

  private readonly gridApi$$ = new BehaviorSubject<GridApi | null>(null);
  readonly gridApi$ = this.gridApi$$.asObservable().pipe(filter(Boolean));

  private readonly isServerSideRowModel$ = toObservable(this.gridOptions).pipe(
    map((options) => options.rowModelType === 'serverSide'),
    shareOneReplay(),
  );

  private readonly serverSideTotalRowCount$ = this.gridApi$.pipe(
    switchMap((api) =>
      fromEvent(api as GridApi & HasEventTargetAddRemove<unknown>, 'firstDataRendered').pipe(
        withLatestFrom(this.isServerSideRowModel$),
        map(([, isServerSideRowModel]): number | null => {
          if (!isServerSideRowModel) {
            return null;
          }
          return api.paginationGetRowCount();
        }),
      ),
    ),
    startWith(null),
    shareOneReplay(),
  );

  private readonly hasData$ = this.isServerSideRowModel$.pipe(
    switchMap((isServerSideRowModel) => {
      if (isServerSideRowModel) {
        return this.serverSideTotalRowCount$.pipe(map(Boolean));
      }
      return this.service.items$.pipe(map((items) => !!items?.length));
    }),
    shareOneReplay(),
  );

  readonly state = connectState({
    data: this.service.items$,
    hasData: this.hasData$,
    isLoading: this.service.loading$.pipe(distinctUntilChanged()),
    renderAgGrid: combineLatest([this.hasData$, this.isServerSideRowModel$, this.serverSideTotalRowCount$]).pipe(
      map(
        ([hasData, isServerSideRowModel, serverSideTotalRowCount]) =>
          hasData || (isServerSideRowModel && serverSideTotalRowCount === null),
      ),
      distinctUntilChanged(),
      tap((renderAgGrid) => {
        if (!renderAgGrid) {
          this.onGridDestroy();
        }
      }),
    ),
    initialized: combineLatest([this.service.loading$.pipe(distinctUntilChanged()), this.incrementalLoading$]).pipe(
      switchMap(([loading, incrementalLoading]) => {
        if (!incrementalLoading) {
          return of(loading);
        }

        if (!loading) {
          return of(false);
        }

        return this.service.items$.pipe(map((items) => items.length === 0));
      }),
      distinctUntilChanged(),
      map((loading) => !loading),
    ),
  });

  ngOnInit() {
    merge(
      this.searchCtrl.valueChanges.pipe(
        startWith(this.searchCtrl.value),
        debounceTime(100),
        switchMap((value) =>
          this.gridApi$.pipe(
            take(1),
            tap((gridApi) => gridApi.setGridOption('quickFilterText', value)),
          ),
        ),
      ),
      this.gridApi$.pipe(
        switchMap((api) =>
          fromEvent(api as HasEventTargetAddRemove<unknown>, 'gridSizeChanged').pipe(tap(() => api.sizeColumnsToFit())),
        ),
      ),
    )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  onGridReady(params: GridReadyEvent) {
    const gridApi = params.api;
    this.gridApi$$.next(gridApi);
    this.service.setGridApi(gridApi);
    this.gridReady.emit(params);
  }

  onGridDestroy() {
    this.gridApi$$.next(null);
    this.service.setGridApi(null);
    this.gridDestroy.emit();
  }

  rowClicked(event: RowClickedEvent) {
    this.gridRowClicked.emit(event);
    this.service.edit?.(event);
  }

  rowDoubleClicked(event: RowDoubleClickedEvent) {
    this.gridRowDoubleClicked.emit(event);
  }
}
